/**
 * Modal dialog (desktop) / bottom sheet (phone), the one dialog implementation
 * of the app. Focus is trapped inside, Esc closes the top-most dialog, focus
 * returns to the opener on close, and the page behind does not scroll.
 */
import clsx from 'clsx';
import { X } from 'lucide-react';
import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useIsPhone } from '../lib/media.js';
import styles from './Dialog.module.css';

const FOCUSABLE =
  'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

const TEXT_ENTRY = /^(INPUT|TEXTAREA|SELECT)$/;

/** Open dialogs, innermost last (only that one reacts to Esc / traps Tab), and their panels. */
const stack: string[] = [];
const panels = new Map<string, HTMLElement>();
/** The page's own overflow style while dialogs lock its scrolling. */
let bodyOverflow = '';
/** Where to return focus when a dialog's opener is gone (see the cleanup in DialogInner). */
const focusFallback = new Map<string, HTMLElement>();

/** Focusable descendants; `:disabled` also covers controls disabled through a `<fieldset disabled>`. */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => !el.matches(':disabled') && !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true');
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  /** Maximum width in px of the centred modal (instead of `size`). */
  width?: number;
  /** 'auto': bottom sheet on phones, centred modal otherwise. */
  variant?: 'auto' | 'modal' | 'sheet';
  /** Element to focus on open (default: an element marked `data-autofocus`, else the first focusable in the body, else the panel). */
  initialFocus?: RefObject<HTMLElement | null>;
  /**
   * When false, a text field is not focused on open (the panel is): on a phone
   * that would pop up the keyboard over a sheet that is mostly read.
   */
  autoFocusInput?: boolean;
  /** When false, Esc and backdrop clicks do not close (e.g. while saving). */
  dismissible?: boolean;
  /** Element to focus after closing instead of the opener (e.g. when the action replaced the opener). */
  returnFocus?: () => HTMLElement | null | undefined;
  className?: string;
}

export function Dialog(props: DialogProps) {
  if (!props.open) return null;
  return <DialogInner {...props} />;
}

function DialogInner({
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  width,
  variant = 'auto',
  initialFocus,
  autoFocusInput = true,
  dismissible = true,
  returnFocus,
  className,
}: DialogProps) {
  const id = useId();
  const titleId = `${id}-title`;
  const descId = `${id}-desc`;
  const panelRef = useRef<HTMLDivElement>(null);
  const isPhone = useIsPhone();
  const sheet = variant === 'sheet' || (variant === 'auto' && isPhone);
  const onCloseRef = useRef(onClose);
  const dismissibleRef = useRef(dismissible);
  const returnFocusRef = useRef(returnFocus);
  onCloseRef.current = onClose;
  dismissibleRef.current = dismissible;
  returnFocusRef.current = returnFocus;

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const body = document.body;
    if (stack.length === 0) {
      bodyOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
    }
    stack.push(id);
    const panel = panelRef.current!;
    panels.set(id, panel);
    const target =
      initialFocus?.current ??
      panel.querySelector<HTMLElement>('[data-autofocus]:not(:disabled)') ??
      focusableWithin(panel.querySelector('[data-dialog-body]') ?? panel)[0] ??
      panel;
    (autoFocusInput || !TEXT_ENTRY.test(target.tagName) ? target : panel).focus({ preventScroll: true });
    // focus() is silently ignored by an element that cannot take focus (e.g. a disabled `initialFocus`).
    if (!panel.contains(document.activeElement)) panel.focus({ preventScroll: true });

    const onKeyDown = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== id) return;
      if (e.key === 'Escape') {
        if (dismissibleRef.current) {
          e.preventDefault();
          onCloseRef.current();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusableWithin(panel);
      if (items.length === 0) {
        e.preventDefault();
        panel.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      if (e.shiftKey && (active === first || !panel.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !panel.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      const i = stack.lastIndexOf(id);
      if (i >= 0) stack.splice(i, 1);
      panels.delete(id);
      const back = [returnFocusRef.current?.(), opener, focusFallback.get(id)].find((el) => el?.isConnected);
      focusFallback.delete(id);
      const top = stack[stack.length - 1];
      if (top === undefined) {
        body.style.overflow = bodyOverflow;
        back?.focus({ preventScroll: true });
      } else if (back && panels.get(top)?.contains(back)) {
        // A nested dialog closed: back into the dialog below.
        back.focus({ preventScroll: true });
      } else if (back) {
        // Another dialog opened as this one closed (a follow-up question): focus stays there, and
        // returns here when that one closes, even if its own opener was a button of this dialog.
        focusFallback.set(top, back);
      }
    };
    // Mount/unmount only (latest callbacks are read through refs): focus must not jump on re-render.
  }, []);

  return createPortal(
    <div className={clsx(styles.backdrop, sheet && styles.backdropSheet)} onMouseDown={(e) => e.target === e.currentTarget && dismissible && onClose()}>
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={clsx(styles.panel, sheet ? styles.sheet : styles.modal, !sheet && width === undefined && styles[size], className)}
        style={!sheet && width !== undefined ? { maxWidth: width } : undefined}
      >
        {sheet && <div className={styles.grabber} aria-hidden="true" />}
        <header className={styles.header}>
          <div className={styles.headings}>
            <h2 id={titleId} className={styles.title}>
              {title}
            </h2>
            {description && (
              <p id={descId} className={styles.description}>
                {description}
              </p>
            )}
          </div>
          {dismissible && (
            <button type="button" className={clsx('btn btn-ghost btn-icon', styles.close)} onClick={onClose} aria-label="Sluiten">
              <X aria-hidden="true" />
            </button>
          )}
        </header>
        <div className={styles.body} data-dialog-body="">
          {children}
        </div>
        {footer && <footer className={styles.footer}>{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}

/** Bottom sheet regardless of screen size (e.g. the phone "Meer" menu). */
export function Sheet(props: Omit<DialogProps, 'variant'>) {
  return <Dialog {...props} variant="sheet" />;
}
